/*
* @Description: 长连接实例。可恶因为时间和业务的原因，用不了socket，逻辑就复杂太多了
* @Author: FengQi
* @Email: zhaolong.dzl@alibaba-inc.com
* @Date:   2018-11-05 18:45:36
* @Last Modified by:   FengQi
* @Last Modified time: 2018-12-10 15:06:13
* @Copyright(c) 2018 Taobao.com
*/

import EventEmitter from 'events';
import JSONP from '@ali/tarzan-jsonp';
import { encode, queryToString, getURL, genUrl, Jsonp, Cors, request } from './util';

// 轮询最大重试次数
const LOOP_MAX_NUM = 5;

// 心跳间隔
const PING_INTERVAL = 15 * 1000;

// 消息发送的超时时间，超过 5 秒无响应则认为失败
const SEND_FAIL_TIMEOUT = 25 * 1000;

const DEFAULT_DOMAIN = {
  method: "CORS",
  connected: "//connected.daily.taobao.net",
  harbor: "//webim.daily.taobao.net"
};

class Connect extends EventEmitter {

  constructor(options = {}) {
    super();

    this.debug = !!options.debug;

    this.options = options;

    this.domain = options.domain || DEFAULT_DOMAIN;

    this._params = options.params;

    this.userId = options.userId;

    this.userName = options.userName;

    // 收到的最近 10 条消息，用于消息必达的去重
    this._lastTenMsgIds = [];

    /**
     * 是否开通消息必达功能，ping 里会随时更新这个值
     * 1: 开通，0: 关闭
     */
    this._isDeliveredOn = options.delivered ? 1 : 0;

    /**
     * 是否开通“已读未读”功能，ping 里会随时更新这个值
     * 1: 开通，0: 关闭
     */
    this._isReadStatusOn = options.readStatus ? 1 : 0;

    this._pingInterval = options.pingInterval || PING_INTERVAL;

    // 心跳定时器
    this._pingTimer = null;

    // 轮询实例
    this._loopInstance = null;

    // 重试次数
    this._loopNum = 0;

    // 轮询开始的时间
    this._loopTime = null;

    // 轮询的定时器
    this._loopTimer = null;

    // 开启心跳
    this._ping();

    // 开始轮询
    this._loopStart();
  }

  _ping() {
    const timer = () => {
      this._request("ping", {
        //支持xspace单元路由
        "xsp_tenantid": this._params.tenantId,
        "xsp_departmentid": this._params.departmentId
      }).then(resp => {
        this._log('ping success');
        // 更新消息必达功能的开关状态
        this._isDeliveredOn = +(resp.delivered || 0);
        // 更新已读未读功能的开关状态
        this._isReadStatusOn = +(resp.supportNewFunction || 0);
      }, e => {
        this._log('ping fail');
      });

      clearTimeout(this._pingTimer);
      this._pingTimer = setTimeout(timer, this._pingInterval);
    };

    this._pingTimer = setTimeout(timer, this._pingInterval);
  }

  _loopStart() {
    this._loopTime = new Date();
    // 如果检测超过一段时间没有收到消息，再强制再启动一次轮询
    // @静无 说，此处超时时间为 60 秒
    this._loopTimer = setInterval(() => {
      if (new Date() - this._loopTime > SEND_FAIL_TIMEOUT) {
        // 防止一直调接口
        this._loopNum++;
        this._log('loop TimeOut, no response', 'num:' + this._loopNum);

        this._loop();
      }
    }, SEND_FAIL_TIMEOUT + 500);

    this._loop();
  }

  /**
   * 获取消息的长轮询，接口会在16 * 1000后响应
   */
  _loop() {

    // 接口错误不能狂调接口，这个容错好像不能加
    /*if(this._loopNum >= LOOP_MAX_NUM) {
      this.close()
      return
    }*/

    const url = genUrl(getURL('polling', this.domain), this._mergeParams({
      //支持xspace单元路由
      "xsp_tenantid": this._params.tenantId,
      "xsp_departmentid": this._params.departmentId,
      senderId: this.userId,
      senderName: this.userName,
      rtt: this.domain.timestamp, // 添加路由版本号
      delivered: this._isDeliveredOn // 表明开通“消息必达”功能
    }));

    // 开始新的轮询之前，先取消之前的轮询
    this._cancelLoop();

    this._log('loop start');
    // 会员端使用 jsonp
    this._loopInstance = JSONP(url, {
      // 长连接超时时间 23s
      timeout: SEND_FAIL_TIMEOUT - 2 * 1000,
      charset: 'utf-8'
    }, this._handleResponse.bind(this));
  }

  _handleResponse(err, resp) {
    const url = getURL('polling', this.domain);

    if (err || !resp) {
      if (err && !err.code) {}
      // 在服务器记录出错信息，正常的业务接口的错误(含err.code)则不report，小二端需要
      /*this._request('reportHttpRequestIssue', {
        requestUrl: encode(url),
        responseError: encode(err && err.toString())
      })*/


      // 防止一直调接口
      this._loopNum++;
      this._log('loop fail', '2num:' + this._loopNum, err);

      // 即使返回错误的结果，也需要再重新发送轮询
      this._startNewLoop();
    } else {
      if (resp && (resp.status == 0 || resp.success === false)) {
        err = new Error(resp.message || resp.errMsg);
        err.code = resp.code || resp.errCode;
      }

      if (err) {
        // 在服务器记录出错信息，正常的业务接口的错误(含err.code)则不report，小二端需要
        /*this._request('reportHttpRequestIssue', {
          requestUrl: encode(url),
          responseError: encode(err && err.toString())
        })*/

        // 防止一直调接口
        this._loopNum++;
        this._log('loop fail', 'num:' + this._loopNum, err);

        // 即使返回错误的结果或空信息时，也需要再重新发送轮询
        return this._startNewLoop();
      }

      if (resp !== undefined && Object.prototype.toString.call(resp) !== '[object Array]') {
        // 如果不是数组，记录一下，便于排查问题
        /*if (window.tracker) {
          window.tracker.log({
            code: 19,
            msg: 'ocs message receives wrong data',
            c1: JSON.stringify(resp),
            c2: url
          })
        }*/
        // 然后强制改为空数组
        resp = [];
      }

      // 请求成功后就重置
      this._loopNum = 0;
      this._log('loop success');

      // 去重
      const msgList = resp && this._removeDuplicateMsg(resp);
      const mids = msgList.map(item => (item.head || {}).mid);
      if (!mids.length) return this._startNewLoop();

      // 更新最近的 10 条消息
      this._storeNewestMsg(mids);

      // 推送消息
      this.emit('message', msgList);

      // 没开通必达，则重新开始 loop 即可，不必再发 ack
      if (!this._isDeliveredOn) return this._startNewLoop();

      // 过滤需要发送 ack 的消息类型
      const midsShouldAck = msgList.filter(item => !~['TypingStart', 'TypingEnd', 'PreMSG', 'Queue'].indexOf(item.cmd)).map(item => (item.head || {}).mid);

      if (!midsShouldAck.length) return this._startNewLoop();

      // 发送 ack 请求
      this._ack({
        mids: midsShouldAck
      }).then(data => {
        this._startNewLoop();
      }, err => {
        this._startNewLoop();
      });
    }
  }

  _startNewLoop() {
    // 如果已经 close 了，则不再重新轮询了
    if (!this._loopTimer) return;
    this._loopTime = new Date();
    this._loop();
  }

  /**
   * 消息必达开通后，删去有可能收到的重复消息，返回一个新数组
   * @param msgList {Array} loop 收到的消息数组
   * @return {Array}
   */
  _removeDuplicateMsg(msgList = []) {
    return msgList.filter(item => {
      const { mid } = item.head || {};
      return !~this._lastTenMsgIds.indexOf(mid);
    });
  }

  /**
   * 保存最近的 10 条消息 ID
   * @param msgIds {Array} 去重后的消息数组 ID
   */
  _storeNewestMsg(msgIds) {
    const newList = this._lastTenMsgIds.concat(msgIds);
    const len = newList.length;
    this._lastTenMsgIds = len > 10 ? newList.slice(len - 10) : newList;
  }

  /**
   * 消息必达的 ack 请求，当收到消息后，告诉服务器已经收到
   * @param mids {Array}  消息 id 数组
   */
  _ack({ mids } = {}) {
    return this._request('ack', {
      sid: this._params.sid,
      senderId: this.userId,
      senderName: this.userName,
      mids
    }, {
      timeout: 3000
    });
  }

  _mergeParams(data = {}) {
    return Object.assign({}, this._params, data);
  }

  _request(name, params = {}, options = {}) {
    return request(name, this.domain, this._mergeParams(params), options);
  }

  _log(...args) {
    if (this.debug) {
      console.log(...args);
    }
  }

  _cancelLoop() {
    if (this._loopInstance && typeof this._loopInstance == 'function') {
      this._loopInstance();
    }
  }

  close() {
    // 停止轮询
    this._cancelLoop();

    //停止防断检测
    if (this._loopTimer) {
      clearInterval(this._loopTimer);
      this._loopTimer = null;
    }

    // 停止ping
    if (this._pingTimer) {
      clearTimeout(this._pingTimer);
      this._pingTimer = null;
    }

    this.removeAllListeners();
  }

}

export default (options => new Connect(options));


// WEBPACK FOOTER //
// ./node_modules/_@ali_im-sdk-third@0.1.8@@ali/im-sdk-third/lib/connect.js